import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { IoDocuments } from 'react-icons/io5';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
    fetchWrapper,
    getChannelIdFromSettingsURL,
    getStandardDate,
    getTeamIdFromURL,
    SafeJSONParse,
} from '../../../../../_helpers';
import { VscLoading } from 'react-icons/vsc';
import styled from '@emotion/styled';
import SubmissionViewer from './SubmissionViewer';
import { connect } from 'react-redux';
const StyledModalDialog = styled.div`
    &.modal-90w {
        width: 90%;
        max-width: none !important;
        background: #fff;
        margin: auto;
    }
`;
// const groupByDate = (data) => {
//     const res = [{ sticky: true, value: 'day' }];
//     const dates = {};

//     (data || []).forEach((item) => {
//         if (item?.createdAt) {
//             const value = dayjs(item.createdAt).format('MMMM D, YYYY') + ':';
//             if (!dates[value]) {
//                 dates[value] = value;
//                 res.push({ value, day: true });
//             } else {
//                 res.push({ value: '' });
//             }
//         }
//     });

//     return { values: res };
// };

function AccumulatedView({ form, sortBy, channel, startDate, endDate, user, teams }) {
    const channelIdFromUrl = getChannelIdFromSettingsURL();
    const teamId = getTeamIdFromURL();
    const teamInfo = useMemo(() => {
        const name = (teams || []).find((team) => (team?._id || team?.id) === teamId)?.name || '';
        return {
            name,
            teamId,
        };
    }, [teams, teamId]);

    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);
    const isFormSelected = useMemo(() => !!(form && (!Array.isArray(form) || form.length)), [form]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setTables([]);
            let url = `userFormData?sortBy=createdAt:asc&limit=3000&page=1`;

            if (!form || (Array.isArray(form) && form.length === 0)) {
                return;
            }

            if (Array.isArray(form)) {
                url += `&forms=${form.map((f) => f.value).join(',')}`;
            } else {
                url += `&form=${form.value}`;
            }

            if (user?.value) {
                url += `&user=${user.value}`;
            }
            if (channel?.value || channelIdFromUrl) {
                url += `&chatroom=${channelIdFromUrl || channel?.value}`;
            } else if (teamId) {
                url += `&team=${teamId}`;
            }
            if (startDate) {
                url += `&startDate=${startDate}`;
            }
            if (endDate) {
                url += `&endDate=${endDate}`;
            }

            const response = await fetchWrapper.get(url);
            if (response) {
                const { results = [] } = response;

                // Group results by form if multiple forms then by client/channel
                const groupedResults = Array.isArray(form)
                    ? form.map((f) => results.filter((r) => r.form === f.value))
                    : [results];

                const allFormsOutput = [];

                groupedResults.forEach((formResults, formIndex) => {
                    if (!formResults.length) return;

                    const currentForm = Array.isArray(form) ? form[formIndex] : form;
                    // Further group by client/chatroom so that each table
                    // represents one client's submissions for this form.
                    const resultsByClient = formResults.reduce((acc, submission) => {
                        const clientId = submission?.chatroomData?.id || submission?.chatroomData?._id || 'unknown';
                        if (!acc[clientId]) {
                            acc[clientId] = {
                                submissions: [],
                                chatroomData: submission.chatroomData || {},
                            };
                        }
                        acc[clientId].submissions.push(submission);
                        return acc;
                    }, {});

                    Object.values(resultsByClient).forEach(({ submissions, chatroomData }) => {
                        if (!submissions.length) return;
                        const firstResult =
                            submissions.find(
                                (userFormData) =>
                                    userFormData?.specialityForm &&
                                    ((userFormData?.specialityForm || {})?.extraInfo || {})?.summary
                            ) ||
                            submissions[0] ||
                            {};
                        const specialityType = (firstResult?.specialityForm || {})?.type;
                        const fields = [
                            { fieldName: 'Submitted Date', sticky: true },
                            { fieldName: 'Submitted Time', sticky: true },
                            { fieldName: 'Submitted By', sticky: true },
                        ];
                        const data = [];

                        // Derive all field headers from the first entry
                        let formFields = (firstResult?.formData?.data || []).filter((f) => f.key !== 'Header');

                        // Track column indexes for clientMedications-specific log layout
                        let medicationColumnIndex = null;
                        let signatureColumnIndex = null;

                        // For clientMedications speciality forms, do not create columns for header/meta
                        // fields that we will instead show once at the bottom (client info and
                        // medication instructions).
                        if (specialityType === 'clientMedications') {
                            formFields = formFields.filter((f) => {
                                const specialityKey = f?.specialityKey || (f?.config || {})?.specialityKey;
                                const headerKeys = [
                                    'clientName',
                                    'allergies',
                                    'practitionerName',
                                    'diagnosis',
                                    'dob',
                                    'medicationInfo',
                                ];
                                return !headerKeys.includes(specialityKey);
                            });

                            // Determine which resulting columns correspond to Medication and Signature
                            formFields.forEach((field, idx) => {
                                const specialityKey = field?.specialityKey || (field?.config || {})?.specialityKey;
                                if (specialityKey === 'medication') {
                                    medicationColumnIndex = 3 + idx;
                                }
                                if (field?.key === 'Signature') {
                                    signatureColumnIndex = 3 + idx;
                                }
                            });
                        }

                        formFields.forEach((f) => fields.push({ fieldName: f.fieldName }));

                        // Build each row (one row per submission for this client)
                        submissions.forEach((submission) => {
                            const { userData, createdAt, data: userFormData } = submission;
                            const row = [];
                            row.push({ value: getStandardDate(createdAt), sticky: true });
                            row.push({ value: dayjs(createdAt).format('h:mm A'), sticky: true });
                            row.push({ value: userData?.name || 'Deleted User', sticky: true });

                            formFields.forEach((field) => {
                                const { key: fieldType, _id, data: fieldData } = field;
                                const matchedField = userFormData?.find((f) => _id === f.fieldName.split('-')[1]);
                                const userDataValue = matchedField?.value;

                                const actualValue = fieldData?.find((f) => f?.value === userDataValue)?.text;
                                let image;
                                let value = actualValue || matchedField?.text || userDataValue || '';
                                if ((field?.options || {})?.enableDataValue) {
                                    value = fieldData?.find((f) => f?.value === userDataValue)?.dataValue || value;
                                }

                                try {
                                    switch (fieldType) {
                                        case 'FullName':
                                            const fullName = JSON.parse(value);
                                            value = `${fullName?.firstName || ''} ${fullName?.lastName || ''}`;
                                            break;
                                        case 'Signature':
                                        case 'Image':
                                            image = value;
                                            value = undefined;
                                            break;
                                        case 'Checkbox':
                                            const parsed = SafeJSONParse(value) || [];
                                            if ((field?.options || {})?.enableDataValue) {
                                                value = parsed
                                                    .map((v) => fieldData?.find((f) => f?.value === v)?.dataValue)
                                                    .join(', ');
                                            } else {
                                                value = parsed
                                                    .map((v) => fieldData?.find((f) => f?.value === v)?.text)
                                                    .join(', ');
                                            }
                                            break;
                                        case 'ServiceRadio':
                                            value = SafeJSONParse(value)?.HCPCS || value;
                                            break;
                                        default:
                                            if (fieldType.toLowerCase().includes('date')) {
                                                value = dayjs(value).format('MMMM D, YYYY h:mm A');
                                            } else if (fieldType.toLowerCase().includes('time')) {
                                                value = dayjs(value).format('h:mm A');
                                            }
                                            break;
                                    }
                                } catch (e) {
                                    console.error(e);
                                }

                                row.push(image ? { image } : { value: value || '' });
                            });

                            data.push(row);
                        });

                        // For speciality forms (e.g. clientMedications), capture any persisted
                        // speciality summary and header meta so they can be shown once at
                        // the bottom of the log format view and included in generated PDFs.
                        let specialitySummaryArr = [];
                        let specialityHeader = '';
                        let medicationOrders = null;

                        if (specialityType === 'clientMedications') {
                            const rawSummary =
                                (firstResult?.specialityForm?.extraInfo &&
                                    firstResult.specialityForm.extraInfo.summary) ||
                                '';

                            if (rawSummary) {
                                const lines = rawSummary
                                    .split('\n')
                                    .map((line) => line.trim())
                                    .filter(Boolean);

                                // Only keep prescribing doctor dosage/frequency lines to
                                // avoid rendering the full (lengthy) FDA instructions in
                                // log format and its PDFs.
                                const filtered = lines.filter((line) => {
                                    const lower = line.toLowerCase();
                                    return (
                                        lower.startsWith('prescribed dosage (doctor order)') ||
                                        lower.startsWith('prescribed frequency (doctor order)')
                                    );
                                });
                                console.log('filtered', filtered);

                                specialitySummaryArr = filtered;
                            }
                            console.log('specialitySummaryArr', specialitySummaryArr);
                            try {
                                const allFields = firstResult?.formData?.data || [];
                                const headerFields = allFields.filter((f) => {
                                    const specialityKey = f?.specialityKey || (f?.config || {})?.specialityKey;
                                    return ['clientName', 'allergies', 'practitionerName', 'diagnosis', 'dob'].includes(
                                        specialityKey
                                    );
                                });

                                const headerLines = [];

                                headerFields.forEach((field) => {
                                    const fieldId = field._id?.toString();
                                    const specialityKey = field?.specialityKey || (field?.config || {})?.specialityKey;

                                    let value = '';
                                    // Search across this client's submissions for the
                                    // first non-empty value for the header field.
                                    for (const submission of submissions) {
                                        const submissionData = submission.data || [];
                                        const matched = submissionData.find((f) => {
                                            const trackId = (f.fieldName || '').split('-')?.at(-1);
                                            return trackId && trackId === fieldId;
                                        });
                                        const candidate = (matched?.value || '').trim();
                                        if (candidate) {
                                            value = candidate;
                                            break;
                                        }
                                    }

                                    if (!value) return;

                                    if (specialityKey === 'dob') {
                                        value = dayjs(value).isValid() ? dayjs(value).format('MMMM D, YYYY') : value;
                                    }

                                    const labelMap = {
                                        clientName: "Client's Name",
                                        allergies: 'Allergies',
                                        practitionerName: 'Healthcare Practitioner Name',
                                        diagnosis: 'Diagnosis',
                                        dob: 'DOB',
                                    };
                                    const label = field.fieldName || labelMap[specialityKey] || specialityKey;
                                    headerLines.push(`${label}: ${value}`);
                                });

                                specialityHeader = headerLines.join('\n');
                            } catch (e) {
                                // swallow errors; header is optional
                                // eslint-disable-next-line no-console
                                console.error('Failed to derive clientMedications header info for log format', e);
                            }

                            // Build per-medication dosage / frequency map from
                            // specialityForm.data so that the horizontal log
                            // can show the correct doctor-prescribed values
                            // for each medication instead of reusing a single
                            // summary for all rows.
                            medicationOrders = {};
                            submissions.forEach((submission) => {
                                const specialityData = (submission?.specialityForm || {}).data || {};
                                console.log(specialityData);
                                const medName = (specialityData.medicationName || '').trim();
                                const dosage = (specialityData.dosage || '').trim();
                                const frequency = (specialityData.frequency || '').trim();
                                if (!medName) return;

                                if (!medicationOrders[medName]) {
                                    medicationOrders[medName] = { dosage: '', frequency: '' };
                                }
                                if (dosage && !medicationOrders[medName].dosage) {
                                    medicationOrders[medName].dosage = dosage;
                                }
                                if (frequency && !medicationOrders[medName].frequency) {
                                    medicationOrders[medName].frequency = frequency;
                                }
                            });
                        }

                        const clientLabel = chatroomData?.title ? ` - ${chatroomData.title}` : '';

                        allFormsOutput.push({
                            formName: `${currentForm.label}${clientLabel}`,
                            formId: currentForm.value,
                            fields,
                            data,
                            specialityType,
                            specialitySummaryArr,
                            specialityHeader,
                            medicationColumnIndex,
                            signatureColumnIndex,
                            medicationOrders,
                        });
                    });
                });

                setTables(allFormsOutput);
            }
        } catch (error) {
            toast.error(error.message);
            if (error.name === 'AbortError') {
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && form && startDate && endDate) {
            fetchData();
        }
    }, [show, form, user?.value, channel?.value, startDate, endDate]);

    return (
        <Fragment>
            <Button onClick={handleShow}>
                <IoDocuments />
                <span className="ml-2">Log Format</span>
            </Button>
            <Modal show={show} onHide={handleClose} dialogClassName="modal-90w" dialogAs={StyledModalDialog}>
                <Modal.Header closeButton className="h3 text-center">
                    <Modal.Title className="modal-headers"> Log Format</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isFormSelected || !startDate || !endDate ? (
                        <Fragment>
                            <div>
                                {!isFormSelected && <p>- Please select a form !</p>}
                                {!startDate && <p>- Please select a start date !</p>}
                                {!endDate && <p>- Please select a end date !</p>}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            {loading ? (
                                <div className="text-center">
                                    <VscLoading className="spin" size={50} />
                                </div>
                            ) : (
                                <SubmissionViewer tables={tables} user={user} teamInfo={teamInfo} />
                            )}
                        </Fragment>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
}

const mapStateToProps = (state) => ({
    position: state.user.position,
    teams: state.teams.teams,
});

export default connect(mapStateToProps, {})(AccumulatedView);
